import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { CardDetailsProps } from "@/interfaces/Card";
import CompanyCourseStatus, {
  UpdateCompanyCourseStatusForm,
} from "@/interfaces/CompanyCourseStatus";
import {
  useGetCompanyCourseStatusQuery,
  useUpdateCompanyCourseStatusMutation,
} from "@/services/companies/courses/statuses";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const CompanyCourseStatusEditCard: FC<
  CardDetailsProps<CompanyCourseStatus>
> = ({ id }) => {
  const { data, isLoading, isError, error } =
    useGetCompanyCourseStatusQuery(id);
  const [updateCompanyCourseStatusAsync] =
    useUpdateCompanyCourseStatusMutation();
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UpdateCompanyCourseStatusForm>();

  useEffect(() => {
    data && reset(data);
  }, [data]);

  const onSubmit: SubmitHandler<UpdateCompanyCourseStatusForm> = async (
    newData
  ) => {
    const result =
      getDifferenceBetweenTwoObjects<UpdateCompanyCourseStatusForm>(
        data,
        newData
      );
    await toast.promise(updateCompanyCourseStatusAsync([id, result]).unwrap(), {
      pending: "Modification du statut de l'entreprise en cours ...",
      success: {
        render() {
          return (
            <p className="mb-0 text-center">
              Le statut de l'entreprise à bien été modifiée 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Vérifiez que les champs sont correctement remplis et que le nom de l'utilisateur n'existe pas déjà";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<UpdateCompanyCourseStatusForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification du statut de l'entreprise" />
        <Card.Body className="bg-light">
          <Row className="gx-3">
            <Col lg={12}>
              <Controller
                control={control}
                name="backgroundColor"
                render={({ field: { value: backgroundColor } }) => (
                  <Controller
                    control={control}
                    name="textColor"
                    render={({ field: { value: color } }) => (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Nom : <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          placeholder="ex : En attente"
                          type="text"
                          style={{ backgroundColor, color }}
                          {...register("name", {
                            required: {
                              value: true,
                              message: INVALID_NAME_MESSAGE,
                            },
                            minLength: {
                              value: 2,
                              message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                            },
                            maxLength: {
                              value: 255,
                              message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                            },
                          })}
                          isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  />
                )}
              />
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Séléctionne la couleur du texte{" "}
                  <span className="text-danger">*</span> :{" "}
                </Form.Label>
                <Form.Control
                  type="color"
                  className="w-100"
                  {...register("textColor", {
                    required: "Ce champ est obligatoire",
                  })}
                  isInvalid={!!errors.textColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.textColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Séléctionne la couleur de fond{" "}
                  <span className="text-danger">*</span> :{" "}
                </Form.Label>
                <Form.Control
                  type="color"
                  className="w-100"
                  {...register("backgroundColor", {
                    required: "Ce champ est obligatoire",
                  })}
                  isInvalid={!!errors.backgroundColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.backgroundColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="mt-3 me-3"
          >
            Confirmer
          </Button>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default CompanyCourseStatusEditCard;
